import { Component, effect, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { RegistrierungService } from '../../services/registrierung.service';
import { PasswordStrengthService } from '../../services/password-strength.service';
import { ZugangsdatenMTO } from '../../generated-model/zugangsdatenMTO';
import { RegistrierungComponent } from '../registrierung.component';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatInput, MatSuffix } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { MatProgressBar } from '@angular/material/progress-bar';

@Component({
  selector: 'app-zugangsdaten',
  imports: [
    FormsModule,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatFormField,
    MatIcon,
    MatSuffix,
    MatError,
    TranslatePipe,
    MatProgressBar,
  ],
  templateUrl: './zugangsdaten.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './zugangsdaten.component.scss',
})
export class ZugangsdatenComponent {
  private readonly registrierungService = inject(RegistrierungService);
  private readonly passwordStrengthService = inject(PasswordStrengthService);
  private readonly MIN_CHAR_BENUTZERNAME = 6;

  registrierungComponent = inject(RegistrierungComponent);

  // Signal inputs and state
  formGroupZugangsdaten = input<FormGroup>({} as FormGroup);
  passwortstaerke = signal(0);
  showpassWort = signal<boolean>(false);
  showBenutzerName = signal<boolean>(false);

  constructor() {
    // Effect to sync email from registrierungComponent
    effect(() => {
      const fg = this.formGroupZugangsdaten();
      if (fg) {
        fg.patchValue({
          benutzername: this.registrierungComponent.registrierungEmail(),
        });
      }
    });

    // Setup auto-save effect on form value changes
    effect(() => {
      const fg = this.formGroupZugangsdaten();
      if (fg?.valueChanges) {
        fg.valueChanges
          .pipe(
            debounceTime(200),
            switchMap((value) => {
              return this.updateZugangsdaten(value);
            })
          )
          .subscribe((result) => {
            if (result === false) {
              console.log('formular failure: autosave not executed');
            }
          });
      }
    });
  }

  private updateZugangsdaten(value: any) {
    const zugangsdaten = {} as ZugangsdatenMTO;
    zugangsdaten.benutzername = value.benutzername;
    zugangsdaten.passwort = value.passwort;
    zugangsdaten.passwort_wdh = value.passwort;
    zugangsdaten.geheime_frage = value.geheime_frage;
    zugangsdaten.geheime_antwort = value.geheime_antwort;

    this.registrierungService.registrierungsdaten.zugangsdaten = zugangsdaten;

    if (zugangsdaten.passwort !== null) {
      this.checkPasswortStaerke(value.passwort);
    } else {
      this.passwortstaerke.set(0);
    }

    const save$ = this.registrierungService.saveRegistrierungsdatenToLocalStorage();

    if (zugangsdaten.benutzername !== undefined && zugangsdaten.benutzername.length >= this.MIN_CHAR_BENUTZERNAME) {
      return this.checkZugangsdaten().pipe(switchMap(() => save$));
    }

    return save$;
  }

  checkPasswortStaerke(password: string): void {
    const strength = this.passwordStrengthService.calculateStrength(password);
    this.passwortstaerke.set(strength);
  }

  checkZugangsdaten() {
    return this.registrierungService.checkZugangsdaten(this.registrierungService.registrierungsdaten.zugangsdaten).pipe(
      tap((response) => {
        const benutzernameControl = this.formGroupZugangsdaten().get('benutzername');
        if (!response.success) {
          benutzernameControl?.markAsTouched();
          benutzernameControl?.setErrors({ error: true, invalid: true });
          this.showBenutzerName.set(true);
        } else if (benutzernameControl?.hasError('invalid')) {
          benutzernameControl.setErrors(null);
          this.showBenutzerName.set(false);
        }
      }),
      catchError((error) => {
        console.log('checkZugangsdaten failed', error);
        return of(null);
      })
    );
  }

  onShowPasswortToggle() {
    this.showpassWort.set(!this.showpassWort());
  }

  generierePasswort(length: number = 10) {
    this.showpassWort.set(true);

    const password = this.passwordStrengthService.generatePassword(length);
    const fg = this.formGroupZugangsdaten();
    fg.patchValue({ passwort: password, passwort_wdh: password });
  }
}
